import classNames from 'classnames/bind';
import { Controller, useForm } from 'react-hook-form';

import { Stream } from 'types/stream';

import { useUpdateStream } from 'streams/hooks/useUpdateStream';

import { Button } from 'components/Button/Button';
import { ButtonGroup } from 'components/ButtonGroup/ButtonGroup';
import { Field } from 'components/Field/Field';
import { FormErrorMessage } from 'components/FormMessage/FormMessage';
import { Heading } from 'components/Heading/Heading';
import { InlineNotification } from 'components/InlineNotification/InlineNotification';
import { LightSwitch } from 'components/LightSwitch/LightSwitch';
import { Text } from 'components/Text/Text';

import { Separator } from 'components/Separator/Separator';
import { StreamSummary } from 'streams/components/Summary/Summary';
import styles from './StreamDetail.module.scss';

const c = classNames.bind(styles);

export type StreamDetailSettingsProps = {
  stream: Stream;
};

type StreamSettingsFieldValues = Pick<Stream, 'rtsp_config'>;

export function StreamDetailSettings({ stream }: StreamDetailSettingsProps) {
  const {
    control,
    formState: { isDirty },
    handleSubmit,
    reset,
  } = useForm<StreamSettingsFieldValues>({
    defaultValues: stream,
  });

  const {
    mutate: updateStream,
    isLoading,
    isSuccess,
    error,
  } = useUpdateStream({
    onSuccess({ rtsp_config }) {
      reset({ rtsp_config });
    },
  });

  function onSubmit({ rtsp_config }: StreamSettingsFieldValues) {
    updateStream({ id: stream.id, rtsp_config });
  }

  return (
    <section className={c('settings')}>
      <StreamSummary stream={stream} />

      <Separator />

      <Heading level="3">Network settings</Heading>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Field label="Always use TCP">
          <div className={c('lightswitch')}>
            <Controller
              name="rtsp_config"
              control={control}
              render={({ field: { onChange, value } }) => (
                <LightSwitch
                  onChange={(event) => {
                    if (event.target.checked) {
                      onChange({ protocols: ['tcp'] });
                    } else {
                      onChange(null);
                    }
                  }}
                  checked={Boolean(value)}
                />
              )}
            />
            <Text type="paragraph" asChild>
              <label htmlFor="rtsp_config">
                Enable to use TCP for improved reliability in congested or other
                network situations.
              </label>
            </Text>
          </div>
        </Field>

        <FormErrorMessage error={error} />

        <ButtonGroup>
          <Button
            variant="primary"
            type="submit"
            size="small"
            loading={isLoading}
          >
            Save network settings
          </Button>

          {isDirty && <InlineNotification>Unsaved changes</InlineNotification>}

          {isSuccess && !isDirty && (
            <InlineNotification intent="success">Saved!</InlineNotification>
          )}
        </ButtonGroup>
      </form>
    </section>
  );
}
